import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { Card, RadialBars } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "components/common/CustomSkeleton";
import useSpendingData from "hooks/useSpendingData";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import { formatCashFull } from "utils/formatCash";

export interface TopCategoriesRadialProps {
  /** Window to aggregate over. Matches the spend headline. */
  timeFrame?: TimeFrameEnum;
  /** Whether each arc casts a glow in its own colour in dark mode. */
  glow?: boolean;
}

/** How many rings the panel draws. Past this they stop being readable. */
const MAX_RINGS = 5;

/**
 * Where spend is concentrated by expense category, drawn as concentric arcs,
 * each sized against the largest so a long tail stays legible.
 *
 * The query lives here rather than in `useByocDashboard` because it is the only
 * consumer and its window is local.
 */
const TopCategoriesRadial: React.FC<TopCategoriesRadialProps> = ({
  timeFrame = TimeFrameEnum.LAST_30_DAYS,
  glow = false,
}) => {
  const { t } = useTranslation();

  const { pieData, currency, loading } = useSpendingData({
    type: "pie",
    groupKey: "expenseCategory",
    filters: { timeFrame },
  });

  // The tail is dropped rather than folded into an "Other" ring: an arc
  // aggregating a dozen small categories says nothing about concentration.
  const rings = useMemo(
    () =>
      [...pieData]
        .sort((a, b) => b.value - a.value)
        .slice(0, MAX_RINGS)
        .map((item) => ({
          id: item.name,
          label: item.name,
          value: item.value,
        })),
    [pieData],
  );

  return (
    <Card
      variant="outlined"
      title={t("Top categories")}
      subtitle={t("Largest spend areas")}
      sx={{ height: "100%" }}
    >
      {loading && rings.length === 0 ? (
        <CustomSkeleton variant="graph" />
      ) : rings.length === 0 ? (
        <Typography
          variant="body1"
          sx={{
            color: "var(--color-text-tertiary)",
            textAlign: "center",
            padding: "var(--space-8) 0",
          }}
        >
          {t("No spend in this period")}
        </Typography>
      ) : (
        <RadialBars
          data={rings}
          height={240}
          glow={glow}
          valueFormatter={(datum) =>
            formatCashFull(datum.value, currency || "usd") ?? ""
          }
        />
      )}
    </Card>
  );
};

export default TopCategoriesRadial;
